// import { connectFunctionsEmulator } from 'firebase/functions'; // Inte längre nödvändig för production
import { useState, useCallback } from 'react';
import {
  authenticateUser,
  getAuthenticationSession,
  getHousingInformation,
  getPersonalInformation,
  savePersonalInformation
} from '../api';
import {
  findMatchingHousing,
  updateHousingOwnership,
  createUnverifiedHousing,
  saveAddressToHousing,
  checkUserHasVerifiedAddress,
  searchAllHousing,
  addHousingToUser,
  deleteHousing as deleteHousingAPI
} from '../api/housing';
import {
  backgroundCheckInit,
  BFRApplicationAcceptanceInit,
  BFRApplicationAcceptanceTwoInit,
  fetchApplications,
  fetchApplicationsCreatedBy,
  makeApplicationInit,
  makeApplicationDirect,
  makeApplicationInitStepTwo,
  removeApplicationInit,
  tenantAcceptanceInit,
  requestRentalExtension,
  changeTenant as changeTenantAPI,
  initiateTermination as initiateTerminationAPI
} from '../api/application';
import { ApplicationData, BackgroundCheck, Housing, User } from '../api/types';
import { auth, functions } from '../config/firebase';

export const useAPI = () => {
  const [isLoading, setIsLoading] = useState(false);

  const authenticateWithBankID = async (): Promise<string | undefined> => {
    setIsLoading(true);
    const res = await authenticateUser();
    setIsLoading(false);
    if (res?.errorMessage) {
      // TODO: Create snackbar context and handle error
    }
    return res?.redirectUrl;
  };

  const getAuthSession = async (sessionId: string) => {
    setIsLoading(true);
    const res = await getAuthenticationSession(sessionId);
    setIsLoading(false);
    return res;
  };

  const getUserInfo = async (uid?: string): Promise<User | undefined> => {
    let id = uid;
    if (!id) {
      id = auth.currentUser?.uid;
      if (!id) return;
    }
    setIsLoading(true);
    const { data, errorMessage } = await getPersonalInformation(id);
    setIsLoading(false);

    if (errorMessage && !data) {
      // TODO: Create snackbar context and handle error
    }
    return data;
  };

  const saveUserInfo = async (data: { email: string; phoneNumber: string; firstName?: string; lastName?: string }) => {
    setIsLoading(true);
    if (!auth.currentUser?.uid) return;
    const res = await savePersonalInformation({
      id: auth.currentUser.uid,
      email: data.email,
      phoneNumber: data.phoneNumber,
      firstName: data.firstName,
      lastName: data.lastName
    });
    if (res?.errorMessage) {
      // TODO: Create snackbar context and handle error
      setIsLoading(false);
      return false;
    }
    setIsLoading(false);
    return true;
  };

  const getHousingInfo = async (
    uid?: string
  ): Promise<Housing[] | undefined> => {
    let id = uid;
    if (!id) {
      id = auth.currentUser?.uid;
      if (!id) return;
    }
    setIsLoading(true);
    const { data, errorMessage } = await getHousingInformation(id);
    setIsLoading(false);

    if (errorMessage && !data) {
      // TODO: Create snackbar context and handle error
    }
    return data;
  };

  // Process PersonData och matcha mot housing
  const processPersonDataAddress = async (personData: any, userInfo: any) => {
    try {
      setIsLoading(true);
      
      // Försök hitta matchande lägenhet
      const matchingHousing = await findMatchingHousing(personData);
      
      if (matchingHousing) {
        // Uppdatera befintlig lägenhet med ägarskap
        const result = await updateHousingOwnership(matchingHousing.id, {
          email: userInfo.email,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          uid: auth.currentUser?.uid || ''
        });
        
        if (result.success) {
          return { 
            type: 'MATCHED', 
            data: matchingHousing.data,
            housingCooperative: matchingHousing.data.housingCooperative || '', // Skicka BRF-namn från matchad lägenhet
            message: 'Din lägenhet har kopplats automatiskt!'
          };
        } else {
          throw new Error(result.error || 'Kunde inte uppdatera lägenhet');
        }
      } else {
        // Skapa overifierad lägenhet
        const result = await createUnverifiedHousing(personData, {
          email: userInfo.email,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          uid: auth.currentUser?.uid || ''
        });
        
        if (result.success) {
          return { 
            type: 'CREATED_UNVERIFIED', 
            data: result.data,
            housingCooperative: '', // Ingen BRF-koppling för overifierad lägenhet
            message: 'Ny lägenhet skapad. BRF admin kommer att granska informationen.'
          };
        } else {
          throw new Error(result.error || 'Kunde inte skapa lägenhet');
        }
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Kontrollera om användaren redan har en verifierad adress
  const checkUserVerifiedAddress = async (userId?: string) => {
    let id = userId;
    if (!id) {
      id = auth.currentUser?.uid;
      if (!id) return { hasVerifiedAddress: false };
    }
    
    try {
      const result = await checkUserHasVerifiedAddress(id);
      return result;
    } catch (error: unknown) {
      return { hasVerifiedAddress: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  // Spara adressdata för framtida användning
  const saveAddressData = async (personData: any, userInfo: any, housingCooperative?: string) => {
    try {
      setIsLoading(true);
      
      const result = await saveAddressToHousing(personData, {
        email: userInfo.email,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        uid: auth.currentUser?.uid || ''
      }, housingCooperative);
      
      if (result.success) {
        return { 
          success: true, 
          data: result.data,
          message: 'Adressdata har sparats för framtida användning.'
        };
      } else {
        throw new Error(result.error || 'Kunde inte spara adressdata');
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Sök i alla lägenheter
  const searchHousing = useCallback(async (searchTerm: string = '') => {
    try {
      setIsLoading(true);
      const result = await searchAllHousing(searchTerm);
      if (result.data) {
        return result.data;
      } else {
        throw new Error(result.errorMessage || 'Kunde inte söka lägenheter');
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []); // Memoize för att undvika oändliga loopar

  // Lägg till lägenhet till användarens profil
  const addApartmentToProfile = useCallback(async (housingData: any) => {
    try {
      setIsLoading(true);
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Ingen användare inloggad');
      }

      const result = await addHousingToUser(userId, housingData);
      if (result.success) {
        return result;
      } else {
        throw new Error(result.error || 'Kunde inte lägga till lägenhet');
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []); // Memoize för att undvika oändliga loopar

  const makeApplication = async (application: ApplicationData) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await makeApplicationInit(application);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const makeApplicationDirectly = async (application: ApplicationData) => {
    try {
      setIsLoading(true);
      const result = await makeApplicationDirect(application);
      setIsLoading(false);
      return result;
    } catch (error: any) {
      setIsLoading(false);
      throw error;
    }
  };

  const makeApplicationStepTwo = async (
    application: Pick<
      ApplicationData,
      | 'id'
      | 'tenantEmail'
      | 'tenantPersonalNumber'
      | 'tenantFirstName'
      | 'tenantLastName'
      | 'tenantPhoneNumber'
    >
  ) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await makeApplicationInitStepTwo(application);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const removeApplication = async (
    applicationId: string,
    newToDate: string
  ) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await removeApplicationInit(applicationId, newToDate);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const tenantAcceptance = async (
    application: Pick<ApplicationData, 'id' | 'tenantAccepted'>
  ) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await tenantAcceptanceInit(application);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const BFRApplicationAcceptance = async (data: {
    applicationId: string;
    isAccepted: boolean;
  }) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await BFRApplicationAcceptanceInit(data);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const BFRApplicationAcceptanceTwo = async (data: {
    applicationId: string;
    isAccepted: boolean;
  }) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await BFRApplicationAcceptanceTwoInit(data);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const backgroundCheck = async (backgroundCheck: BackgroundCheck) => {
    // Använder nu production functions istället för emulator
    // if (process.env.NODE_ENV !== 'production') {
    //   connectFunctionsEmulator(functions, 'localhost', 5001);
    // }
    try {
      setIsLoading(true);
      await backgroundCheckInit(backgroundCheck);
      setIsLoading(false);
    } catch (error: any) {
      // TODO: Create snackbar context and handle error
    }
  };

  const getApplications = async (
    uid?: string
  ): Promise<ApplicationData[] | undefined> => {
    let id = uid;
    if (!id) {
      id = auth.currentUser?.uid;
      if (!id) return;
    }

    setIsLoading(true);
    const { data, errorMessage } = await fetchApplications(id);
    setIsLoading(false);

    if (errorMessage && !data) {
      // TODO: Create snackbar context and handle error
    }
    return data;
  };

  const getApplicationsCreatedBy = async (
    uid?: string
  ): Promise<ApplicationData[] | undefined> => {
    let id = uid;
    if (!id) {
      id = auth.currentUser?.uid;
      if (!id) return;
    }

    setIsLoading(true);
    const { data, errorMessage } = await fetchApplicationsCreatedBy(id);
    setIsLoading(false);

    if (errorMessage && !data) {
      // TODO: Create snackbar context and handle error
    }
    return data;
  };

  const deleteHousing = async (housingId: string) => {
    setIsLoading(true);
    try {
      const result = await deleteHousingAPI(housingId);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const extendRental = async (applicationId: string, newEndDate: string) => {
    setIsLoading(true);
    try {
      await requestRentalExtension(applicationId, newEndDate);
    } finally {
      setIsLoading(false);
    }
  };

  const changeTenant = async (applicationId: string, newStartDate: string, newEndDate: string, tenantInfo: any) => {
    setIsLoading(true);
    try {
      const result = await changeTenantAPI(applicationId, newStartDate, newEndDate, tenantInfo);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const initiateTermination = async (applicationId: string, moveOutDate: string, reason?: string) => {
    setIsLoading(true);
    try {
      await initiateTerminationAPI(applicationId, moveOutDate, reason);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    authenticateWithBankID,
    getUserInfo,
    makeApplication,
    makeApplicationDirectly,
    getApplications,
    getApplicationsCreatedBy,
    BFRApplicationAcceptance,
    BFRApplicationAcceptanceTwo,
    getHousingInfo,
    makeApplicationStepTwo,
    tenantAcceptance,
    backgroundCheck,
    removeApplication,
    saveUserInfo,
    getAuthSession,
    processPersonDataAddress,
    checkUserVerifiedAddress,
    saveAddressData,
    searchHousing,
    addApartmentToProfile,
    deleteHousing,
    extendRental,
    changeTenant,
    initiateTermination
  };
};
